import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaDownload, FaExternalLinkAlt } from 'react-icons/fa';
import BillingService, {
  PlanTier,
  BillingInterval,
  BillingStatus,
  BillingState,
  CatalogPlan,
  CatalogAddOn,
  CatalogResponse,
  SummaryUsage,
  EstimateResponse,
  BillingAccount,
  BillingInvoice,
  InvoiceStatus,
  parseDecimal,
} from '../services/BillingService';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';
import BillingSummaryCard from './BillingSummaryCard';
import UsageOverview from './UsageOverview';
import PlanSelection from './PlanSelection';
import BillingConfigurator from './BillingConfigurator';

const INVOICE_STATUS_CONFIG: Record<InvoiceStatus, { label: string; bg: string; color: string }> = {
  paid: { label: 'Bezahlt', bg: colors.successLight, color: colors.success },
  open: { label: 'Offen', bg: colors.warningLight, color: colors.warning },
  draft: { label: 'Entwurf', bg: colors.bgMuted, color: colors.textMuted },
  void: { label: 'Storniert', bg: colors.bgMuted, color: colors.textLight },
  uncollectible: { label: 'Uneinbringlich', bg: colors.dangerLight, color: colors.danger },
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCurrency(amount: number): string {
  return amount.toFixed(2).replace('.', ',') + ' €';
}

interface FormField {
  key: keyof BillingAccount;
  label: string;
  placeholder: string;
  half?: boolean;
}

const addressFormFields: FormField[] = [
  { key: 'billing_email', label: 'Rechnungs-E-Mail', placeholder: 'billing@example.com' },
  { key: 'legal_name', label: 'Firma / Name', placeholder: 'z.B. Mein Restaurant GmbH' },
  { key: 'street', label: 'Straße', placeholder: 'Musterstraße' },
  { key: 'house_no', label: 'Hausnummer', placeholder: '42', half: true },
  { key: 'postal_code', label: 'PLZ', placeholder: '10115', half: true },
  { key: 'city', label: 'Stadt', placeholder: 'Berlin', half: true },
  { key: 'country', label: 'Land', placeholder: 'Deutschland', half: true },
  { key: 'vat_id', label: 'USt-IdNr.', placeholder: 'DE123456789' },
  { key: 'signatory_name', label: 'Zeichnungsberechtigter', placeholder: 'Max Mustermann' },
  { key: 'phone', label: 'Telefon', placeholder: '+49 30 123456' },
];

const EMPTY_ACCOUNT: BillingAccount = {
  billing_email: '', legal_name: '', street: '', house_no: '',
  postal_code: '', city: '', country: '', vat_id: '',
  signatory_name: '', phone: '',
};

const EMPTY_USAGE: SummaryUsage = {
  locations_count: 0, subscribed_locations: 0,
  active_employees_count: 0, max_employees: 0, employees_utilization_percent: 0,
};

function getBillingErrorMessage(err: any): string {
  const code = err?.response?.data?.code || err?.data?.code;
  const detail = err?.response?.data?.detail || err?.data?.detail;
  if (code === 'quota_exceeded') return 'Mitarbeitendenlimit erreicht. Bitte Paket upgraden.';
  if (code === 'feature_not_enabled') return 'Dieses Feature ist in deinem Paket nicht enthalten.';
  if (code === 'downgrade_blocked') return 'Downgrade nicht möglich — aktuelle Nutzung überschreitet Zielpaket.';
  return detail || 'Ein Fehler ist aufgetreten.';
}

export default function BillingCockpit(): React.JSX.Element {
  const [loading, setLoading] = useState(true);
  const configuratorRef = useRef<HTMLDivElement>(null);
  const estimateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [plans, setPlans] = useState<CatalogPlan[]>([]);
  const [addOns, setAddOns] = useState<CatalogAddOn[]>([]);
  const [yearlyAvailable, setYearlyAvailable] = useState(false);
  const [extraPackInfo, setExtraPackInfo] = useState<CatalogResponse['extra_employee_pack'] | null>(null);

  const [hasSubscription, setHasSubscription] = useState(false);
  const [currentTier, setCurrentTier] = useState<PlanTier | null>(null);
  const [selectedTier, setSelectedTier] = useState<PlanTier>('pro');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [billingStatus, setBillingStatus] = useState<BillingStatus>('active');
  const [periodEnd, setPeriodEnd] = useState('');
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [billingState, setBillingState] = useState<BillingState>('none');
  const [isDesignPartner, setIsDesignPartner] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [locationCount, setLocationCount] = useState(0);
  const [extraPacks, setExtraPacks] = useState(0);
  const [enabledAddonSlugs, setEnabledAddonSlugs] = useState<string[]>([]);
  const [usage, setUsage] = useState<SummaryUsage>(EMPTY_USAGE);

  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);

  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const [addressData, setAddressData] = useState<BillingAccount>(EMPTY_ACCOUNT);
  const [originalAddress, setOriginalAddress] = useState<BillingAccount | null>(null);
  const [addressChanged, setAddressChanged] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);

  // --- 3A. Initial Data Load ---
  const loadAllData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [catalogResult, summaryResult, accountResult, invResult] = await Promise.allSettled([
        BillingService.getCatalog(),
        BillingService.getSummary(),
        BillingService.getAccount(),
        BillingService.getInvoices(),
      ]);

      const catalog = catalogResult.status === 'fulfilled' ? catalogResult.value : null;
      const summary = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
      const account = accountResult.status === 'fulfilled' ? accountResult.value : EMPTY_ACCOUNT;
      const inv = invResult.status === 'fulfilled' ? invResult.value : [];

      if (!catalog && !summary) {
        setLoadError('Billing-Daten konnten nicht geladen werden. Bitte später erneut versuchen.');
        return;
      }

      if (catalog) {
        setPlans(catalog.plans ?? []);
        setAddOns(catalog.addons ?? []);
        setYearlyAvailable(catalog.yearly_available ?? false);
        setExtraPackInfo(catalog.extra_employee_pack ?? null);
      } else {
        console.warn('Catalog load failed:', catalogResult.status === 'rejected' ? catalogResult.reason : 'unknown');
      }

      if (summary) {
        setHasSubscription(summary.has_subscription ?? false);
        setUsage(summary.usage ?? EMPTY_USAGE);
        setExtraPacks(summary.extra_employee_packs ?? 0);
        setEnabledAddonSlugs(summary.addons ?? []);
        setBillingState(summary.billing_state ?? 'none');
        setIsDesignPartner(summary.is_design_partner ?? false);
        setTrialDaysRemaining(summary.trial_days_remaining ?? 0);

        if (summary.has_subscription && summary.subscription) {
          const sub = summary.subscription;
          setCurrentTier(sub.plan.tier);
          setSelectedTier(sub.plan.tier);
          setBillingStatus(sub.status);
          setBillingInterval(sub.billing_interval);
          setPeriodEnd(sub.current_period_end);
          setCancelAtPeriodEnd(sub.cancel_at_period_end);
          setTrialEnd(sub.trial_end ?? null);
          setLocationCount(summary.usage?.subscribed_locations ?? 1);
        } else {
          setCurrentTier(null);
          setSelectedTier(catalog?.plans?.[1]?.tier ?? catalog?.plans?.[0]?.tier ?? 'pro');
          setLocationCount(summary.usage?.locations_count || 1);
        }
      }

      setAddressData(account);
      setOriginalAddress(account);
      setInvoices(inv);
    } catch (err) {
      console.error('Failed to load billing data:', err);
      setLoadError('Ein unerwarteter Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => { loadAllData(); }, [loadAllData]);

  // --- 3D. Stripe-Return (fulfill checkout) + auto-refresh on tab focus ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      const url = new URL(window.location.href);
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());

      BillingService.fulfillCheckout(sessionId)
        .then(() => {
          toast.success('Abonnement erfolgreich eingerichtet!');
          loadAllData();
        })
        .catch(() => {
          toast.info('Checkout abgeschlossen — Daten werden aktualisiert.');
          loadAllData();
        });
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadAllData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadAllData]);

  // --- 3B. Debounced Estimate ---
  useEffect(() => {
    if (loading || plans.length === 0) return;
    if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);

    estimateTimerRef.current = setTimeout(async () => {
      setEstimateLoading(true);
      try {
        const result = await BillingService.getEstimate({
          tier: selectedTier,
          extra_employee_packs: extraPacks,
          addon_slugs: enabledAddonSlugs,
          interval: billingInterval,
          locations: locationCount,
        });
        setEstimate(result);
      } catch (err) {
        console.error('Estimate failed:', err);
      } finally {
        setEstimateLoading(false);
      }
    }, 400);

    return () => {
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    };
  }, [selectedTier, billingInterval, extraPacks, enabledAddonSlugs, locationCount, loading, plans.length]);

  // --- 3C. Handlers ---
  const handleSelectPlan = useCallback((tier: PlanTier) => {
    setSelectedTier(tier);
    setTimeout(() => {
      configuratorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleToggleAddOn = useCallback((slug: string) => {
    setEnabledAddonSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }, []);

  const handleApply = useCallback(async () => {
    try {
      if (!hasSubscription) {
        const { checkout_url } = await BillingService.checkout({
          tier: selectedTier,
          success_url: window.location.href + '?session_id={CHECKOUT_SESSION_ID}',
          cancel_url: window.location.href,
          addon_slugs: enabledAddonSlugs,
          extra_employee_packs: extraPacks,
          interval: billingInterval,
        });
        window.open(checkout_url, '_blank');
      } else {
        await BillingService.changePlan(selectedTier, extraPacks, enabledAddonSlugs);
        toast.success('Planänderung erfolgreich übernommen.');
        await loadAllData();
      }
    } catch (err: any) {
      toast.error(getBillingErrorMessage(err));
    }
  }, [hasSubscription, selectedTier, enabledAddonSlugs, extraPacks, billingInterval, loadAllData]);

  const handleStripePortal = useCallback(async () => {
    try {
      const { portal_url } = await BillingService.getBillingPortalUrl(window.location.href);
      window.open(portal_url, '_blank');
    } catch (err: any) {
      toast.error(getBillingErrorMessage(err));
    }
  }, []);

  const handleDownloadInvoice = useCallback((invoice: BillingInvoice) => {
    const url = invoice.pdf_url || invoice.hosted_invoice_url;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.info('Kein PDF verfügbar für diese Rechnung.');
    }
  }, []);

  const handleAddressChange = useCallback((key: keyof BillingAccount, value: string) => {
    setAddressData((prev) => ({ ...prev, [key]: value }));
    setAddressChanged(true);
  }, []);

  const handleAddressSave = useCallback(async () => {
    setAddressSaving(true);
    try {
      const updated = await BillingService.updateAccount(addressData);
      toast.success('Rechnungsadresse gespeichert');
      setAddressData(updated);
      setOriginalAddress(updated);
      setAddressChanged(false);
    } catch (err: any) {
      toast.error(getBillingErrorMessage(err));
    } finally {
      setAddressSaving(false);
    }
  }, [addressData]);

  const handleAddressCancel = useCallback(() => {
    if (originalAddress) {
      setAddressData({ ...originalAddress });
      setAddressChanged(false);
    }
  }, [originalAddress]);

  const planName = hasSubscription && currentTier
    ? (plans.find((p) => p.tier === currentTier)?.name ?? currentTier.charAt(0).toUpperCase() + currentTier.slice(1))
    : '';

  const monthlyTotal = hasSubscription
    ? (estimate?.total_monthly ?? '0.00')
    : '0.00';

  const applyLabel = hasSubscription ? 'Änderungen übernehmen' : 'Jetzt abonnieren';

  if (loading) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ ...ps.card, padding: 60, textAlign: 'center', color: colors.textMuted }}>
          Billing-Daten werden geladen...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ ...ps.card, padding: 60, textAlign: 'center' }}>
          <div style={{ color: colors.danger, fontSize: 14, marginBottom: 16 }}>{loadError}</div>
          <button
            onClick={loadAllData}
            className="hover-btn-primary"
            style={ps.buttonPrimary}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Section 1 — Billing Summary */}
      <BillingSummaryCard
        planName={planName}
        billingStatus={billingStatus}
        periodEnd={periodEnd}
        monthlyTotal={monthlyTotal}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        trialEnd={trialEnd}
        hasSubscription={hasSubscription}
        onStripePortal={handleStripePortal}
        billingState={billingState}
        trialDaysRemaining={trialDaysRemaining}
      />

      {/* Section 2 — Usage Overview */}
      <UsageOverview usage={usage} />

      {/* Section 3 — Plan Selection */}
      <PlanSelection
        plans={plans}
        currentTier={currentTier}
        selectedTier={selectedTier}
        billingInterval={billingInterval}
        onSelectPlan={handleSelectPlan}
        isDesignPartner={isDesignPartner}
        onSelectDesignPartner={() => handleSelectPlan('premium')}
      />

      {/* Section 4 — Configurator */}
      <div ref={configuratorRef}>
        <BillingConfigurator
          selectedTier={selectedTier}
          billingInterval={billingInterval}
          yearlyAvailable={yearlyAvailable}
          locationCount={locationCount}
          extraPacks={extraPacks}
          addOns={addOns}
          enabledAddonSlugs={enabledAddonSlugs}
          extraPackInfo={extraPackInfo}
          estimate={estimate}
          estimateLoading={estimateLoading}
          onBillingIntervalChange={setBillingInterval}
          onLocationCountChange={setLocationCount}
          onExtraPacksChange={setExtraPacks}
          onToggleAddOn={handleToggleAddOn}
          onApply={handleApply}
          applyLabel={applyLabel}
        />
      </div>

      {/* Section 5 — Invoices & Billing Address */}
      <div style={{ ...ps.card, padding: 24 }}>
        <div style={ps.sectionHeader}>
          <div style={ps.sectionDot(colors.navy)} />
          Rechnungen & Verwaltung
        </div>

        {/* Invoice Table */}
        <div
          style={{
            border: `1px solid ${colors.border}`, borderRadius: 12,
            overflow: 'hidden', marginBottom: 28,
          }}
        >
          <div
            style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
              padding: '12px 20px', background: colors.bgMuted,
              borderBottom: `1px solid ${colors.border}`,
              fontSize: 11, fontWeight: 600, color: colors.textLight,
              textTransform: 'uppercase', letterSpacing: '0.03em',
            }}
          >
            <div>Rechnungsnr.</div>
            <div>Datum</div>
            <div>Betrag</div>
            <div>Status</div>
            <div />
          </div>

          {invoicesLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading...</div>
          ) : invoices.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
              Noch keine Rechnungen vorhanden.
            </div>
          ) : (
            invoices.map((invoice) => {
              const statusConf = INVOICE_STATUS_CONFIG[invoice.status] ?? INVOICE_STATUS_CONFIG.open;
              return (
                <div
                  key={invoice.stripe_invoice_id}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
                    padding: '14px 20px', borderBottom: `1px solid ${colors.borderLight}`,
                    alignItems: 'center', fontSize: 13, transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontWeight: 600, color: colors.navy }}>{invoice.invoice_number || '—'}</div>
                  <div style={{ color: colors.textBody }}>{formatDate(invoice.period_start || '')}</div>
                  <div style={{ fontWeight: 600, color: colors.navy }}>{formatCurrency(parseDecimal(invoice.amount_due))}</div>
                  <div>
                    <span style={ps.badge(statusConf.bg, statusConf.color)}>{statusConf.label}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => handleDownloadInvoice(invoice)}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: colors.textMuted, padding: 6, borderRadius: 6,
                        transition: 'color 0.15s ease',
                      }}
                      title="PDF herunterladen"
                      onMouseEnter={(e) => { e.currentTarget.style.color = colors.magenta; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = colors.textMuted; }}
                    >
                      <FaDownload size={14} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Billing Address Form */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ ...ps.sectionHeader, marginTop: 8 }}>
            <div style={ps.sectionDot(colors.magenta)} />
            Rechnungsadresse
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
            {addressFormFields.map((field) => (
              <div key={field.key} style={{ gridColumn: field.half ? 'auto' : '1 / -1' }}>
                <div style={ps.fieldWrapper}>
                  <label style={ps.labelStyle}>{field.label}</label>
                  <input
                    type="text"
                    value={addressData[field.key]}
                    onChange={(e) => handleAddressChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    style={ps.inputStyle}
                  />
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'flex', gap: 10, justifyContent: 'flex-end',
              paddingTop: 20, borderTop: `1px solid ${colors.borderLight}`,
            }}
          >
            <button
              onClick={handleAddressCancel}
              disabled={!addressChanged || addressSaving}
              className="hover-btn-secondary"
              style={{
                ...ps.buttonSecondary,
                opacity: addressChanged && !addressSaving ? 1 : 0.4,
                cursor: addressChanged && !addressSaving ? 'pointer' : 'not-allowed',
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleAddressSave}
              disabled={!addressChanged || addressSaving}
              className="hover-btn-primary"
              style={{
                ...ps.buttonPrimary,
                opacity: addressChanged && !addressSaving ? 1 : 0.4,
                cursor: addressChanged && !addressSaving ? 'pointer' : 'not-allowed',
              }}
            >
              {addressSaving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Stripe Portal Link */}
        {hasSubscription && (
          <div style={{ textAlign: 'center', paddingTop: 16, borderTop: `1px solid ${colors.borderLight}` }}>
            <button
              onClick={handleStripePortal}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 13, fontWeight: 600, color: colors.magenta, padding: '4px 0',
              }}
            >
              <FaExternalLinkAlt size={11} />
              Stripe Billing Portal öffnen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
